/**
 * Procedural ground material (2026-09-14, q-client-terrain-consumer-build) — the
 * thin GLSL half of groundPalette.ts. WebGL2 + GLSL via `onBeforeCompile` on a
 * `MeshStandardMaterial` — the DEFAULT-path implementation; groundNodeMaterial.ts is
 * the WebGPU/TSL sibling, because WebGPURenderer never calls onBeforeCompile, and
 * both read `groundOctaves()` below so the tiers cannot drift (AD-651 amendment).
 * The GLSL keeps the standard PBR lighting for free — so the ground
 * keeps the standard PBR lighting, shadows, fog and tone mapping for free and
 * only its albedo and roughness become a function of WORLD position.
 *
 * The field, per fragment (world xz, metres):
 *   - macro  : 3-octave value-noise fbm at `macroMetres` blends base ↔ patch
 *              (grass ↔ soil) — the large variation that reads at game distance.
 *   - accent : 2-octave fbm at `accentMetres`, thresholded softly, lays accent
 *              flecks (dry grass / grit) — the mid scale that reads at close range.
 *   - grain  : one value-noise octave at `grainMetres`, ±`grainAmount` luminance —
 *              the fine reference that makes scale and foot-sliding legible on
 *              video. It is faded out by its own screen-space footprint
 *              (`fwidth`, core in GLSL ES 3.0) so it never shimmers at distance.
 *   - roughness follows the macro field inside the palette's band.
 *
 * Deterministic by construction: no time uniform, no texture, sin-free hashing
 * (Hoskins hash12 — `fract(sin())` loses precision at large world coordinates on
 * mediump GPUs), world coordinates carried as a highp varying.
 *
 * Tiers: `full` (desktop) is the field above; `lite` (mobile / ?quality=low)
 * drops the macro fbm to 2 octaves and the accent to 1 — 7 → 4 value-noise taps
 * per fragment. Both share one program per tier (`customProgramCacheKey`).
 * Cost measured 2026-09-14 on the RTX 3070 Laptop (see the story's evidence
 * docs/gates/evidence/showcase-and-ground-material.verify.json).
 *
 * Import boundary (AD-122): frontend-local; `three` is allowed in frontend/.
 */

import * as THREE from 'three';

import type { GroundAlbedoHandle } from './groundAlbedo.js';
import { resolveGroundPalette, type GroundPalette } from './groundPalette.js';

export type GroundDetail = 'full' | 'lite';

/**
 * The octave plan per tier — ONE source for both siblings (the GLSL below and the
 * TSL graph in groundNodeMaterial.ts): `full` = 3 macro / 2 accent, `lite` = 2 / 1
 * (7 → 4 value-noise taps per fragment).
 */
export function groundOctaves(detail: GroundDetail): { macro: number; accent: number } {
  return detail === 'full' ? { macro: 3, accent: 2 } : { macro: 2, accent: 1 };
}

/**
 * Either sibling, as the scene builder consumes it: the WebGL2 default
 * (`createGroundMaterial`) or the WebGPU node material, chosen on
 * `createdRenderer.kind` by Game — the seam post-FX already forks on.
 */
export type GroundMaterialFactory = (
  palette?: GroundPalette | string | null,
  options?: GroundMaterialOptions,
) => THREE.Material;

export interface GroundMaterialOptions {
  /** `lite` for the mobile / explicit-low quality profile. Default `full`. */
  detail?: GroundDetail;
  /** Depth-bias the ground behind coplanar asset bases (the builder's existing fix). */
  polygonOffset?: boolean;
  /**
   * A tileable ground texture (groundAlbedo.ts). Present ⇒ the texture supplies the detail and the
   * grid-aligned accent/grain noise is dropped; absent ⇒ the procedural ground, unchanged.
   */
  albedo?: GroundAlbedoHandle | null;
}

/** The uniforms one ground material owns (exposed for tests/dev pages to read). */
export interface GroundUniforms {
  uGroundBase: { value: THREE.Color };
  uGroundPatch: { value: THREE.Color };
  uGroundAccent: { value: THREE.Color };
  uGroundRough: { value: THREE.Vector2 };
  /** x: 1/macro, y: 1/accent, z: 1/grain, w: grain amount. */
  uGroundScale: { value: THREE.Vector4 };
}

/** The extra uniforms a TEXTURED ground owns. `uGroundMapReady` is shared across materials. */
export interface GroundTextureUniforms {
  uGroundMap: { value: THREE.Texture };
  uGroundMapReady: { value: number };
  /** 1 / world metres per texture repeat. */
  uGroundTile: { value: number };
  /** The texture's mean colour in linear light (the tint denominator). */
  uGroundMapMean: { value: THREE.Vector3 };
}

const VERTEX_DECL = /* glsl */ `
varying highp vec3 vGroundWorld;
`;

const VERTEX_BODY = /* glsl */ `
#include <begin_vertex>
vGroundWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;
`;

const FRAGMENT_DECL = /* glsl */ `
varying highp vec3 vGroundWorld;
uniform vec3 uGroundBase;
uniform vec3 uGroundPatch;
uniform vec3 uGroundAccent;
uniform vec2 uGroundRough;
uniform vec4 uGroundScale;
#ifdef TGC_GROUND_MAP
uniform sampler2D uGroundMap;
uniform float uGroundMapReady;
uniform float uGroundTile;
uniform vec3 uGroundMapMean;
#endif

highp float groundHash(highp vec2 p) {
  highp vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

highp float groundNoise(highp vec2 p) {
  highp vec2 i = floor(p);
  highp vec2 f = fract(p);
  highp vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(groundHash(i), groundHash(i + vec2(1.0, 0.0)), u.x),
    mix(groundHash(i + vec2(0.0, 1.0)), groundHash(i + vec2(1.0, 1.0)), u.x),
    u.y
  );
}

highp float groundFbm(highp vec2 p, int octaves) {
  highp float sum = 0.0;
  highp float amp = 0.5;
  highp float norm = 0.0;
  for (int o = 0; o < 3; o++) {
    if (o >= octaves) break;
    sum += amp * groundNoise(p);
    norm += amp;
    // A rotation-ish offset per octave so lattice axes do not stack visibly.
    p = mat2(1.6, 1.2, -1.2, 1.6) * p + 17.0;
    amp *= 0.5;
  }
  return sum / norm;
}
`;

/**
 * The textured variant. The texture carries all fine detail; the palette carries hue and the
 * large patches, from ONE smooth octave (the higher octaves are what drew grid-aligned squares).
 * A second sample, rotated ~37° and scaled, is blended in to break the repeat at distance. While
 * the image has not uploaded (`uGroundMapReady` = 0) the procedural field is shown instead, so a
 * slow or failed load never leaves a black floor.
 */
function texturedBody(detail: GroundDetail): string {
  return `${proceduralBody(detail)}
highp vec2 gUv = gP * uGroundTile;
highp vec2 gUv2 = mat2(0.7986, 0.6018, -0.6018, 0.7986) * gUv * 0.43 + vec2(0.37, 0.71);
vec3 gTex = mix(texture2D(uGroundMap, gUv).rgb, texture2D(uGroundMap, gUv2).rgb, 0.35);
float gSmooth = groundNoise(gP * uGroundScale.x);
vec3 gTint = mix(uGroundBase, uGroundPatch, smoothstep(0.35, 0.75, gSmooth) * 0.85)
  / max(uGroundMapMean, vec3(1e-3));
gAlbedo = mix(gAlbedo, gTex * gTint, uGroundMapReady);
gGrain = mix(gGrain, 0.0, uGroundMapReady);
diffuseColor = vec4( gAlbedo, opacity );
`;
}

function fragmentBody(detail: GroundDetail, textured = false): string {
  return textured ? texturedBody(detail) : proceduralBody(detail);
}

function proceduralBody(detail: GroundDetail): string {
  const { macro: macroOctaves, accent: accentOctaves } = groundOctaves(detail);
  return /* glsl */ `
highp vec2 gP = vGroundWorld.xz;
float gMacro = groundFbm(gP * uGroundScale.x, ${macroOctaves});
float gMacroT = smoothstep(0.42, 0.72, gMacro) * 0.85;
vec3 gAlbedo = mix(uGroundBase, uGroundPatch, gMacroT);
float gAccent = groundFbm(gP * uGroundScale.y + 91.7, ${accentOctaves});
gAlbedo = mix(gAlbedo, uGroundAccent, smoothstep(0.56, 0.78, gAccent) * 0.45);
highp vec2 gGrainP = gP * uGroundScale.z;
// Fade the grain once one grain cell spans under ~2 pixels (anti-shimmer).
float gFoot = max(fwidth(gGrainP.x), fwidth(gGrainP.y));
float gGrainFade = 1.0 - smoothstep(0.25, 0.6, gFoot);
float gGrain = (groundNoise(gGrainP) - 0.5) * 2.0 * uGroundScale.w * gGrainFade;
gAlbedo *= 1.0 + gGrain;
vec4 diffuseColor = vec4( gAlbedo, opacity );
`;
}

const ROUGHNESS_BODY = /* glsl */ `
float roughnessFactor = mix(uGroundRough.x, uGroundRough.y, clamp(gMacro * 1.2 - 0.1 + gGrain, 0.0, 1.0));
`;

/**
 * Patch GLSL source. Exported (pure string work, no GL) so a test can prove every
 * anchor it relies on was actually replaced — a silent miss would render the
 * stock flat material and nobody would notice.
 */
export function patchGroundShader(
  vertexShader: string,
  fragmentShader: string,
  detail: GroundDetail,
  textured = false,
): { vertexShader: string; fragmentShader: string } {
  const replaceOnce = (src: string, anchor: string, next: string, stage: string): string => {
    if (!src.includes(anchor)) {
      throw new Error(`groundMaterial: ${stage} shader has no '${anchor}' anchor`);
    }
    return src.replace(anchor, next);
  };
  let vs = replaceOnce(
    vertexShader,
    '#include <common>',
    `#include <common>\n${VERTEX_DECL}`,
    'vertex',
  );
  vs = replaceOnce(vs, '#include <begin_vertex>', VERTEX_BODY, 'vertex');
  let fs = replaceOnce(
    fragmentShader,
    '#include <common>',
    `#include <common>\n${textured ? '#define TGC_GROUND_MAP\n' : ''}${FRAGMENT_DECL}`,
    'fragment',
  );
  fs = replaceOnce(
    fs,
    'vec4 diffuseColor = vec4( diffuse, opacity );',
    fragmentBody(detail, textured),
    'fragment',
  );
  fs = replaceOnce(fs, '#include <roughnessmap_fragment>', ROUGHNESS_BODY, 'fragment');
  return { vertexShader: vs, fragmentShader: fs };
}

/** Build the uniform block for a palette. */
export function groundUniforms(palette: GroundPalette): GroundUniforms {
  return {
    uGroundBase: { value: new THREE.Color(palette.base) },
    uGroundPatch: { value: new THREE.Color(palette.patch) },
    uGroundAccent: { value: new THREE.Color(palette.accent) },
    uGroundRough: { value: new THREE.Vector2(palette.roughness[0], palette.roughness[1]) },
    uGroundScale: {
      value: new THREE.Vector4(
        1 / palette.macroMetres,
        1 / palette.accentMetres,
        1 / palette.grainMetres,
        palette.grainAmount,
      ),
    },
  };
}

/**
 * A `MeshStandardMaterial` whose albedo and roughness are the procedural ground
 * field for `palette` (an id or a resolved palette; absent ⇒ the default).
 */
export function createGroundMaterial(
  palette?: GroundPalette | string | null,
  options: GroundMaterialOptions = {},
): THREE.MeshStandardMaterial {
  const resolved =
    typeof palette === 'object' && palette !== null ? palette : resolveGroundPalette(palette);
  const detail = options.detail ?? 'full';
  const material = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: 1,
    metalness: 0,
    ...(options.polygonOffset
      ? { polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1 }
      : {}),
  });
  const uniforms = groundUniforms(resolved);
  const albedo = options.albedo ?? null;
  const textureUniforms: GroundTextureUniforms | null = albedo
    ? {
        uGroundMap: { value: albedo.texture },
        // The SAME object across every material: one load flips every ground mesh together.
        uGroundMapReady: albedo.ready,
        uGroundTile: { value: 1 / albedo.albedo.tileMetres },
        uGroundMapMean: { value: new THREE.Vector3(...albedo.albedo.meanLinear) },
      }
    : null;
  const textured = textureUniforms !== null;
  material.name = `ground:${resolved.id}:${detail}${textured ? ':textured' : ''}`;
  material.userData.groundUniforms = uniforms;
  if (textureUniforms) material.userData.groundTextureUniforms = textureUniforms;
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms, textureUniforms ?? {});
    const patched = patchGroundShader(shader.vertexShader, shader.fragmentShader, detail, textured);
    shader.vertexShader = patched.vertexShader;
    shader.fragmentShader = patched.fragmentShader;
  };
  material.customProgramCacheKey = () => `tgc-ground-${detail}${textured ? '-textured' : ''}`;
  return material;
}
